import { CreditCard, Plus, Trash2, Shield, Check } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "./ui/dialog";
import { useState, useEffect } from "react";
import { SharetribeRepository } from "../infrastructure/repositories/SharetribeRepository";
import { apiService } from "@/services";
import { secureStorage } from "@/utils/secureStorage";
import { ConfigManager } from '../application/config/Config';

// Stripe.js
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";

var config = ConfigManager.getInstance().getApiConfig();
// Your publishable key - loaded from environment variable
const stripePromise = loadStripe(config.stripePublishableKey || "");

// ============================================================
// OUTER WRAPPER: wraps content inside <Elements />
// ============================================================
export function PaymentMethodsContent() {
  return (
    <Elements stripe={stripePromise}>
      <PaymentMethodsInnerContent />
    </Elements>
  );
}

// ============================================================
// REAL CONTENT COMPONENT
// ============================================================
function PaymentMethodsInnerContent() {
  const stripe = useStripe();
  const elements = useElements();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [dialogState, setDialogState] = useState<{
    open: boolean;
    title: string;
    message: string;
    variant: "default" | "destructive";
  }>({
    open: false,
    title: "",
    message: "",
    variant: "default"
  });

  // Stripe state
  const [stripePaymentMethodData, setStripePaymentMethodData] = useState<any>(null);

  // Billing-only fields now (card fields are removed)
  const [formData, setFormData] = useState({
    holderName: "",
    street: "",
    apartment: "",
    postal: "",
    city: "",
    state: "",
    country: "United States"
  });

  const repo = new SharetribeRepository(apiService);
  const userEmail =
  secureStorage.get<string>("current_user_email") ?? "";

  // ------------------------------------------
  // Fetch Stripe Customer + Payment Method
  // ------------------------------------------
  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    setLoading(true);
    try {
      const res = await repo.sharedTribecurrentUserShow(false, true, true);

      if (res && !res.code) {
        const responseData = res.data || res;
        const stripeCustomerRef = responseData.relationships?.stripeCustomer?.data;

        if (stripeCustomerRef) {
          const included = res.included || [];

          const stripeCustomerObj = included.find(
            (item: any) =>
              item.type === "stripeCustomer" && item.id === stripeCustomerRef.id
          );

          const defaultPaymentMethodRef =
            stripeCustomerObj?.relationships?.defaultPaymentMethod?.data;

          if (defaultPaymentMethodRef) {
            const pmObj = included.find(
              (item: any) =>
                item.type === "stripePaymentMethod" &&
                item.id === defaultPaymentMethodRef.id
            );

            if (pmObj && pmObj.attributes?.card) {
              setStripePaymentMethodData(pmObj.attributes.card);
            } else {
              setStripePaymentMethodData(null);
            }
          } else {
            const pmObj = included.find(
              (item: any) => item.type === "stripePaymentMethod"
            );

            if (pmObj && pmObj.attributes?.card) {
              setStripePaymentMethodData(pmObj.attributes.card);
            } else {
              setStripePaymentMethodData(null);
            }
          }
        } else {
          setStripePaymentMethodData(null);
        }
      }
    } catch (err) {
      // Error is handled silently - user will see empty state
      // In production, consider logging to error tracking service
      setStripePaymentMethodData(null);
    } finally {
      setLoading(false);
    }
  };

    // -------------------------
  // Helpers: input & validation
  // -------------------------
  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const showDialog = ({
    title,
    message,
    variant = "default"
  }: {
    title: string;
    message: string;
    variant?: "default" | "destructive";
  }) => {
    setDialogState({ open: true, title, message, variant });
  };

  const closeDialog = () => {
    setDialogState((prev) => ({ ...prev, open: false }));
  };

  const isValidString = (value: string): boolean => {
    return typeof value === "string" && value.trim().length > 0;
  };


  const validateBilling = (): boolean => {
    // basic billing validation (card is validated by Stripe Elements)
    if (!isValidString(formData.holderName)) {
      showDialog({
        title: "Invalid Input",
        message: "Invalid Card Holder's Name",
        variant: "destructive"
      });
      return false;
    }
    if (!isValidString(formData.street)) {
      showDialog({
        title: "Invalid Input",
        message: "Invalid Street Address",
        variant: "destructive"
      });
      return false;
    }
    if (!/^\d{5}$/.test(formData.postal || "")) {
      showDialog({
        title: "Invalid Input",
        message: "Invalid Postal Code",
        variant: "destructive"
      });
      return false;
    }
    if (!isValidString(formData.country)) {
      showDialog({
        title: "Invalid Input",
        message: "Invalid Country",
        variant: "destructive"
      });
      return false;
    }
    return true;
  };

  // ------------------------------------------
  // Handle Save (Stripe.js confirm + Flex create/add)
  // ------------------------------------------
  const handleSavePaymentMethod = async () => {
    if (!stripe || !elements) {
      showDialog({
        title: "Payment Error",
        message: "Stripe is not loaded yet. Please try again.",
        variant: "destructive"
      });
      return;
    }

    if (!validateBilling()) return;

    setSaving(true);
    try {
      // STEP 1 — Create SetupIntent via Flex
      const setupIntentRes = await repo.stripeSetupIntentsCreate();
      if (!setupIntentRes?.data?.attributes) {
        showDialog({
          title: "Payment Error",
          message: "Failed to create setup intent. Please try again later.",
          variant: "destructive"
        });
        setSaving(false);
        return;
      }

      const clientSecret = setupIntentRes.data.attributes.clientSecret;
      // const stripeSetupIntentId = setupIntentRes.data.attributes.stripeSetupIntentId; // not needed for web confirm

      // STEP 2 — Confirm with Stripe.js
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        showDialog({
          title: "Payment Error",
          message: "Card element not mounted",
          variant: "destructive"
        });
        setSaving(false);
        return;
      }

      const { setupIntent, error } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: formData.holderName,
            email: userEmail,
            address: {
              line1: formData.street,
              line2: formData.apartment || "",
              city: formData.city || "",
              state: formData.state || "",
              postal_code: formData.postal || "",
              country: "US"
            }
          }
        }
      });

      if (error) {
        showDialog({
          title: "Payment Error",
          message: error.message || "Failed to confirm card with Stripe. Please check your card details and try again.",
          variant: "destructive"
        });
        setSaving(false);
        return;
      }

      const paymentMethodId = (setupIntent as any)?.payment_method;
      if (!paymentMethodId) {
        showDialog({
          title: "Payment Error",
          message: "Failed to save your payment details. Please try again.",
          variant: "destructive"
        });
        setSaving(false);
        return;
      }

      // STEP 3 — Check if user already has Stripe customer
      const currentUserRes = await repo.sharedTribecurrentUserShow(false, true, true);
      const currentUserData = currentUserRes?.data || currentUserRes;
      const stripeRef = currentUserData?.relationships?.stripeCustomer?.data;

      const hasStripeCustomer =
        stripeRef &&
        typeof stripeRef === "object" &&
        stripeRef.id !== undefined &&
        stripeRef.id !== null;

      // STEP 4 — Create or update Stripe customer on Flex
      let customerRes;
      if (!hasStripeCustomer) {
        customerRes = await repo.stripeCustomerCreate(paymentMethodId, userEmail);
      } else {
        customerRes = await repo.stripeCustomerAddPaymentMethod(paymentMethodId);
      }

      if (customerRes?.code) {
        showDialog({
          title: "Payment Error",
          message: `Failed to save your payment details. Error Code: ${customerRes.code}`,
          variant: "destructive"
        });
        setSaving(false);
        return;
      }

      // SUCCESS
      showDialog({
        title: "Success",
        message: "Your payment details saved successfully!"
      });
      setShowAddForm(false);
      resetForm();
      await fetchPaymentMethods();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error occurred";
      showDialog({
        title: "Payment Error",
        message: `Failed to save your payment details: ${errorMessage}. Please try again later.`,
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  // ------------------------------------------
  // Remove payment method
  // ------------------------------------------
  const handleRemovePaymentMethod = async () => {
    setRemoving(true);
    try {
      const res = await repo.stripeCustomerDeletePaymentMethod();
      if (res?.code) {
        showDialog({
          title: "Payment Error",
          message: `Failed to remove your payment details. Error Code: ${res.code}`,
          variant: "destructive"
        });
      } else {
        showDialog({
          title: "Success",
          message: "Your payment details removed successfully!"
        });
        setShowRemoveDialog(false);
        await fetchPaymentMethods();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error occurred";
      showDialog({
        title: "Payment Error",
        message: `Failed to remove your payment details: ${errorMessage}. Please try again later.`,
        variant: "destructive"
      });
    } finally {
      setRemoving(false);
    }
  };

  // ------------------------------------------
  // Reset form & formatting helpers
  // ------------------------------------------
  const resetForm = () => {
    setFormData({
      holderName: "",
      street: "",
      apartment: "",
      postal: "",
      city: "",
      state: "",
      country: "United States"
    });

    // clear Stripe CardElement if mounted
    try {
      const card = elements?.getElement(CardElement);
      if (card) {
        card.clear();
      }
    } catch (e) {
      // ignore
    }
  };

  const formatCardDisplay = () => {
    if (!stripePaymentMethodData) return "";
    const brand = stripePaymentMethodData.brand || "Card";
    const last4 = stripePaymentMethodData.last4Digits || "****";
    return `${brand.charAt(0).toUpperCase() + brand.slice(1)} •••• ${last4}`;
  };

  const formatExpiry = () => {
    if (!stripePaymentMethodData) return "";
    const month = String(stripePaymentMethodData.expirationMonth || "").padStart(2, "0");
    const year = String(stripePaymentMethodData.expirationYear || "").slice(-2);
    return `${month}/${year}`;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }


    // ============================
  // RENDER
  // ============================
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Payment Methods</h1>
          <p className="text-muted-foreground">Manage your payment methods and billing information</p>
        </div>
        {!stripePaymentMethodData && (
          <Button onClick={() => setShowAddForm(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Payment Method
          </Button>
        )}
      </div>

      {/* Current Payment Method */}
      {stripePaymentMethodData && (
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-8 bg-blue-600 rounded flex items-center justify-center">
                  <CreditCard className="h-4 w-4 text-white" />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <h3 className="font-semibold">{formatCardDisplay()}</h3>
                    <Badge variant="default" className="flex items-center">
                      <Check className="mr-1 h-3 w-3" />
                      Default
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Expires {formatExpiry()}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="text-red-600 hover:text-red-700"
                onClick={() => setShowRemoveDialog(true)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Payment Method Form (uses Stripe CardElement) */}
      {showAddForm && !stripePaymentMethodData && (
        <Card>
          <CardHeader>
            <CardTitle>Payment Card Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-4">
              <div>
                <Label>Card Details</Label>
                <div className="border rounded-md p-3">
                  <CardElement options={{ hidePostalCode: true }} />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Enter your card details. We use Stripe to securely handle card data.
                </p>
              </div>

              <div>
                <Label htmlFor="holderName">Card Holder Name</Label>
                <Input
                  id="holderName"
                  placeholder="Enter your name"
                  value={formData.holderName}
                  onChange={(e) => handleInputChange("holderName", e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-semibold">Billing Address</h3>
              <div>
                <Label htmlFor="street">Street Address</Label>
                <Input
                  id="street"
                  placeholder="Example Street"
                  value={formData.street}
                  onChange={(e) => handleInputChange("street", e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="apartment">Apt (optional)</Label>
                <Input
                  id="apartment"
                  placeholder="A42"
                  value={formData.apartment}
                  onChange={(e) => handleInputChange("apartment", e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="postal">Postal Code</Label>
                  <Input
                    id="postal"
                    placeholder="00100"
                    value={formData.postal}
                    onChange={(e) => handleInputChange("postal", e.target.value.replace(/\D/g, "").slice(0, 5))}
                    maxLength={5}
                  />
                </div>
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    placeholder="Los Angeles"
                    value={formData.city}
                    onChange={(e) => handleInputChange("city", e.target.value)}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="state">State (optional)</Label>
                  <Input
                    id="state"
                    placeholder="Enter your state"
                    value={formData.state}
                    onChange={(e) => handleInputChange("state", e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    value={formData.country}
                    disabled
                    readOnly
                  />
                </div>
              </div>
            </div>

            <p className="text-sm text-muted-foreground">
              I authorise Swimnerd to send instructions to the financial institution
            </p>

            <div className="flex items-center space-x-3 pt-4">
              <Button onClick={handleSavePaymentMethod} disabled={saving}>
                {saving ? "Saving..." : "Save Payment Card"}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddForm(false);
                  resetForm();
                }}
                disabled={saving}
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Payment Method Message */}
      {!showAddForm && !stripePaymentMethodData && (
        <Card>
          <CardContent className="p-6 text-center">
            <CreditCard className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600 mb-4">No payment method added yet</p>
            <Button onClick={() => setShowAddForm(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Payment Method
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Security Information */}
      {/* <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Shield className="mr-2 h-5 w-5" />
            Security & Privacy
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <Check className="h-5 w-5 text-green-500 mt-0.5" />
              <div>
                <p className="font-medium">Encrypted Storage</p>
                <p className="text-sm text-muted-foreground">Your payment information is encrypted and stored securely</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Check className="h-5 w-5 text-green-500 mt-0.5" />
              <div>
                <p className="font-medium">PCI Compliance</p>
                <p className="text-sm text-muted-foreground">We follow industry standards for payment security</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Check className="h-5 w-5 text-green-500 mt-0.5" />
              <div>
                <p className="font-medium">No Storage of CVV</p>
                <p className="text-sm text-muted-foreground">CVV codes are never stored for security reasons</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card> */}

<Card>
  <CardHeader>
    <CardTitle className="flex items-center">
      <Shield className="mr-2 h-5 w-5" />
      Security & Privacy
    </CardTitle>
  </CardHeader>

  <CardContent>
    <div className="space-y-3">
      <div className="flex items-start space-x-3">
        <Check className="h-5 w-5 text-green-500 mt-0.5" />
        <div>
          <p className="font-medium">PCI DSS Compliant</p>
          <p className="text-sm text-muted-foreground">
            Certified to the highest level of PCI DSS security standards.
          </p>
        </div>
      </div>

      <div className="flex items-start space-x-3">
        <Check className="h-5 w-5 text-green-500 mt-0.5" />
        <div>
          <p className="font-medium">No Card or CVV Storage</p>
          <p className="text-sm text-muted-foreground">
            We never store your card number or CVV at any point.
          </p>
        </div>
      </div>

      <div className="flex items-start space-x-3">
        <Check className="h-5 w-5 text-green-500 mt-0.5" />
        <div>
          <p className="font-medium">Encrypted Communication</p>
          <p className="text-sm text-muted-foreground">
            All payment-related communication is encrypted using industry-standard HTTPS.
          </p>
        </div>
      </div>

    </div>
  </CardContent>
</Card>



      {/* Remove Payment Method Dialog */}
      <Dialog open={showRemoveDialog} onOpenChange={setShowRemoveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Payment Card</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>Are you sure you want to remove this payment card?</p>
            <p className="text-sm text-muted-foreground">
              The payment card will be removed from your account.
            </p>
            <div className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => setShowRemoveDialog(false)}
                disabled={removing}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleRemovePaymentMethod}
                disabled={removing}
              >
                {removing ? "Removing..." : "Remove Card"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* General Notification Dialog */}
      <Dialog open={dialogState.open} onOpenChange={(open) => setDialogState((prev) => ({ ...prev, open }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{dialogState.title || "Notice"}</DialogTitle>
            {dialogState.message && <DialogDescription>{dialogState.message}</DialogDescription>}
          </DialogHeader>
          <DialogFooter>
            <Button
              variant={dialogState.variant === "destructive" ? "destructive" : "default"}
              onClick={closeDialog}
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PaymentMethodsContent;
